import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, OnInit, ViewChild, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ModalDismissReasons, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService, CheckoutObject, CheckoutStepComponent, Customer, CustomerService } from 'gung-standard';
import { first, forkJoin, map, mergeMap, Observable } from 'rxjs';
import { CultUnifaunReturnsService, ShipmentPrints } from '../../../../services/cult-unifaun-returns.service';

@Component({
    selector: 'cult-cult-create-unifaun-return-step',
    templateUrl: './cult-create-unifaun-return-step.component.html',
    styleUrls: ['./cult-create-unifaun-return-step.component.scss'],
    standalone: false
})
export class CultCreateUnifaunReturnStepComponent extends CheckoutStepComponent implements OnInit, AfterViewInit {  protected cultUnifaunReturnsService = inject(CultUnifaunReturnsService);
  protected modalService = inject(NgbModal);
  protected customerService = inject(CustomerService);
  protected authService = inject(AuthService);

  modalReference: any;
  shipmentPrints: ShipmentPrints[];
  @ViewChild('quantityValidationModal') validationModal;
  closeModal: string;
  protected VALIDATION_DONE = 'VALIDATION_DONE';
  errorCreatingReturn = false;
  returnResultReceived = false;
  errors: string;

  ngAfterViewInit() {
    // Validate on init and continue if ok
    // Skip validation if the user returned from the next step
    if (!this.checkout.extra.oh?.godsmarke1) {
      this.runValidation();
    } else {
      this.returnResultReceived = true;
    }
  }

  runValidation() {
    this.validate().subscribe(result => {
      result.subscribe({
        next: res => this.handleResult(res, undefined),
        error: err => this.handleResult(undefined, err)
      });
    });
  }

  handleResult(result?: ShipmentPrints[], err?: HttpErrorResponse) {
    if (err) {
      // display error in modal
      this.errors = err.error;
      this.errorCreatingReturn = true;
      console.error(err);
    } else {
      // Add shipmentNos to oh.godsmarke1
      this.checkout.extra.shipmentPrints = result;
      this.checkout.extra.oh.godsmarke1 = result.map(x => x.id).join(',');
      if (result.length > 0) {
        setTimeout(() => {
          this.modalReference.close(this.VALIDATION_DONE);
          this.stepDone.emit(this.checkout);
        }, 100);
      }
    }
    this.returnResultReceived = true;
  }

  ngOnInit() {
    super.ngOnInit();
  }

  triggerModal() {
    this.modalReference = this.modalService.open(this.validationModal, {
      backdrop: 'static',
      size: 'xl',
      keyboard: false,
      ariaLabelledBy: 'modal-basic-title'
    });

    this.modalReference.result.then(
      res => {
        if (res === this.VALIDATION_DONE) {
          this.stepDone.emit(this.checkout);
        } else {
          this.closeModal = `Closed with: ${res}`;
          this.stepPrevious.emit(this.checkout);
        }
      },
      res => {
        this.closeModal = `Dismissed ${this.getDismissReason(res)}`;
        this.stepPrevious.emit(this.checkout);
      }
    );
  }

  private getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'by pressing ESC';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'by clicking on a backdrop';
    } else {
      return `with: ${reason}`;
    }
  }

  protected handleNextButtonClicked(): void {
    // Validate
    this.runValidation();
  }

  protected validate(): Observable<any> {
    this.triggerModal();
    return forkJoin([
      this.authService.getCurrentUser().pipe(first()),
      this.customerService.getCustomer(this.checkout.deliveryCustomerId).pipe(first())
    ]).pipe(
      first(),
      map(([user, customer]) => {
        const returnData = this.createReturnData(customer, user.username);
        return this.cultUnifaunReturnsService.createReturn(returnData);
      })
    );
  }

  getLabel() {
    return 'RETURN_VALIDATION_CREATION';
  }

  public getBtnTexts(checkout: CheckoutObject): { next: string | void; prev: string | void } | void {
    return {
      next: 'TRY_AGAIN',
      prev: null
    };
  }

  private createReturnData(customer: Customer, gungUser: string) {
    const regex = /^[\p{L}]+$/u;
    var city: String;

    //ordlevadr4 can have different formats, this should incomrporate all reasonable formats
    if (regex.test(this.checkout.extra.procargs.ordlevadr4)) {
      city = this.checkout.extra.procargs.ordlevadr4;
    } else {
      const sPart: string[] = this.checkout.extra.procargs.ordlevadr4.split(' ');
      //Format: "12345 CITY"
      if (sPart.length == 2) {
        city = sPart[1];
      }
      //Format: "123 45 CITY"
      else if (sPart.length >=3) {
        city = sPart.slice(2).join(' ');
      }
    }
    return {
      shipment: {
        sender: {
          name: this.checkout.extra.procargs.ordlevadr1,
          address1: this.checkout.extra.procargs.ordlevadr3,
          zipcode: this.checkout.extra.procargs.ftgpostnr,
          city: city.toUpperCase(), //this.checkout.extra.procargs.ordlevadr4.split(' ')[2], //this splits wrong if format is 11122 City, instead of 111 22 city
          country: this.checkout.extra.procargs.ordlevadrlandskod.toUpperCase(),
          contact: this.checkout.extra.oh.kundref2,
          phone: this.checkout.extra?.unifaunReturn?.phone,
          email: this.checkout.extra.confirmationEmails[0] || ''
        },
        service: {
          id: this.checkout.extra.unifaunReturn.ordreturservice,
          normalShipment: true,
          pickupBooking: true,
          pickupDate: this.checkout.extra.unifaunReturn.ordreturdatum,
          pickupText1: this.checkout.extra.unifaunReturn.ordreturinstruktioner
        },
        parcels: this.generateParcels(this.checkout),
        ...this.generateFreeTexts(this.checkout.extra.unifaunReturn.ordreturinstruktioner)
      },
      gungInternal: {
        customerId: customer.id,
        gungUser
      }
    };
  }
  generateFreeTexts(ordreturinstruktioner: string) {
    const splitString = ordreturinstruktioner.match(/(.|[\r\n]){1,30}/g); // splits into chunks of 30 characters
    const freeTexts = {};

    if (splitString != null) {
      splitString.forEach((freeText, index) => {
        freeTexts['freeText' + (index + 1)] = freeText;
      });
    }

    return freeTexts;
  }

  private generateParcels(checkout: CheckoutObject) {
    const result = [];
    for (const packagingRow of checkout.extra.unifaunReturn.packagingRows) {
      result.push({
        contents: 'Retur säsongsartiklar',
        copies: packagingRow.amount,
        packageCode: packagingRow.parcelType,
        volume: (packagingRow.length * packagingRow.width * packagingRow.height) / 1000000,
        valuePerParcel: true,
        weight: packagingRow.weight
      });
    }
    return result;
  }

  openShipmentPdfNewWindow(shipmentPrint: ShipmentPrints) {
    this.cultUnifaunReturnsService.openShipmentPdfNewWindow(shipmentPrint);
  }
}
